import React, { useEffect, useState, Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { setActiveCallLog } from '../../slice/activeCallLogSlice';
import {
    IconEmptyplaceholderCall,
    IconGrayPhone,
    IconGrayVideo,
    IconNewWhiteClose,
    IconWhitePlus,
    IconTrash,
    IconNoInternet,
} from '../../assets/images';
import { useLanguage } from '../../localization/useLanguage';
import { SIDEBAR_MENU_INDEX } from '../../Helpers/Constants';
import { fetchUsersInfo } from '../calls/utils/helper';
import RecentSearch from '../../Components/WebChat/Common/RecentSearch';
import { CALL_LOG_DIRECTION, CALL_TYPE } from '../calls/constants';
import { deleteCallLog, getCallLog, seenCallLog } from '../calls/services/callService';
import { isEnabled } from '../calls/subFeatureRegistry';
import { requestCall } from '../../slice/commonSlice';
import { resetAlertModal, setAlertModal } from '../../channels/utils/alertModalUtils';
import { showSucessToast } from '../../channels/utils/toast';
import { transformAndGroupLogs } from '../calls/utils/callLogUtils';
import './CallLog.scss';
import { setMissedCallCount } from '../calls/model';
import { isConnStateEqualTo } from '../../Helpers/Utility';
import {
    CONNECTION_STATE_CONNECTING,
    CONNECTION_STATE_CONN_FAILED,
    CONNECTION_STATE_ERROR_OCCURED,
} from '../../Helpers/Chat/Constant';
import logger from '../../Helpers/logger';
import CallItemSkeleton from './components/CallItemSkeleton';
import CallLogHeader from './components/CallLogHeader';
import CallLogItem from './components/CallLogItem';
import { useCallLogViewModel } from './hooks/useCallLogViewModel';
import EmptyPlaceholder from './components/EmptyPlaceholder';

const PAGE_SIZE = 20;

const sortCallLogsDesc = (a, b) => {
    let timeA = a.startTime || a.callTime || 0;
    let timeB = b.startTime || b.callTime || 0;
    timeA = timeA > 1000000000000 ? timeA : timeA * 1000;
    timeB = timeB > 1000000000000 ? timeB : timeB * 1000;
    return timeB - timeA;
};

const mergeAndDeduplicateLogs = (prev, newLogs) => {
    const combined = [...prev, ...newLogs];
    const uniqueLogsMap = new Map();
    for (const log of combined) {
        const id = log.callId || log.id || log.call_id || log.startTime || log.callTime;
        if (id) uniqueLogsMap.set(id, log);
    }
    const uniqueLogs = Array.from(uniqueLogsMap.values());
    uniqueLogs.sort(sortCallLogsDesc);
    return uniqueLogs;
};

const getFilteredDataLogs = (data, idsToDelete) => {
    return data
        .map(group => {
            const remainingLogs = group.logs.filter(log => !idsToDelete.includes(log.id));
            return { ...group, logs: remainingLogs };
        })
        .filter(group => group.logs.length > 0);
};

const getFilteredPrevLogs = (prev, idsToDelete) => {
    return prev.filter(log => !idsToDelete.includes(log.callId?.toString()));
};

export const CallScreen = ({ _handleShowCallScreen, handleNewCallNavigation, _handleNewChat }) => {
    const dispatch = useDispatch();
    const [isFabExpanded, setIsFabExpanded] = useState(false);
    const [allCallLogs, setAllCallLogs] = useState([]); // Flat list of all logs
    const [data, setData] = useState([]); // Grouped data for rendering
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination state
    const [loading, setLoading] = useState(true);
    const [isFetchingNext, setIsFetchingNext] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const viewModel = useCallLogViewModel(data);
    const { t } = useLanguage();
    const usersMap = useSelector(state => state.userInfoData.users);
    const isMissedCallEnable = isEnabled('missedCallLog');

    const missedCallCount = useSelector(state => state.callkitData?.missedCallCount || 0);
    const isAppOnline = useSelector(state => state?.appOnlineStatus?.isOnline);
    const connectionState = useSelector(state => state.ConnectionStateData?.data);

    const isOffline = useMemo(() => {
        return (
            !isAppOnline ||
            isConnStateEqualTo([
                CONNECTION_STATE_CONNECTING,
                CONNECTION_STATE_CONN_FAILED,
                CONNECTION_STATE_ERROR_OCCURED,
            ])
        );
    }, [isAppOnline, connectionState]);

    const sendSeenCallLog = () => {
        if (missedCallCount > 0 && isMissedCallEnable) {
            seenCallLog({ callIds: [], markAll: true }, (_success, _err) => {
                dispatch(setMissedCallCount(0));
            });
        }
    };

    const fetchLogs = (lastSyncObj = {}) => {
        getCallLog({ ...lastSyncObj, size: PAGE_SIZE }, (success, err) => {
            if (err) {
                logger.debug('err getCallLog', err);
                setLoading(false);
                setIsFetchingNext(false);
            }
            if (success && success.callLogs && success.callLogs.length > 0) {
                logger.debug('First log object:', success.callLogs[0]);
            }
            const logs = (success && (success.callLogs || success.data)) || (Array.isArray(success) ? success : null);
            if (logs && Array.isArray(logs)) {
                const newLogs = logs;

                setAllCallLogs(prev => {
                    const uniqueLogs = mergeAndDeduplicateLogs(prev, newLogs);
                    const newUniqueLogsCount = uniqueLogs.length - prev.length;
                    if (newUniqueLogsCount === 0 || newLogs.length < PAGE_SIZE) {
                        setHasMore(false);
                    }
                    return uniqueLogs;
                });
            } else {
                setHasMore(false);
            }

            setLoading(false);
            setIsFetchingNext(false);
        });
    };

    const refreshCallLogFlag = useSelector(state => state.callkitData?.refreshCallLogFlag);

    const hasFetched = React.useRef(false);
    const activeStack = useSelector(state => state.menu?.activeMenuIndex);
    const CALL_TAB_INDEX = SIDEBAR_MENU_INDEX.CALL_LOGS;

    const latestLogTimeRef = React.useRef(null);
    useEffect(() => {
        if (allCallLogs.length > 0) {
            latestLogTimeRef.current = allCallLogs[0].startTime || allCallLogs[0].callTime;
        }
    }, [allCallLogs]);

    const prevActiveStackRef = React.useRef(activeStack);
    const prevRefreshFlagRef = React.useRef(refreshCallLogFlag);
    const prevIsAppOnlineRef = React.useRef(isAppOnline);

    useEffect(() => {
        // Only proceed if initial fetch has happened
        if (!hasFetched.current) return;

        const stackChangedToCalls = prevActiveStackRef.current !== CALL_TAB_INDEX && activeStack === CALL_TAB_INDEX;
        const flagToggled = prevRefreshFlagRef.current !== refreshCallLogFlag;
        const becameOnline = !prevIsAppOnlineRef.current && isAppOnline;

        // Update refs to current
        prevActiveStackRef.current = activeStack;
        prevRefreshFlagRef.current = refreshCallLogFlag;
        prevIsAppOnlineRef.current = isAppOnline;

        if (stackChangedToCalls || flagToggled || (becameOnline && activeStack === CALL_TAB_INDEX)) {
            if (latestLogTimeRef.current) {
                fetchLogs({ lastSyncTime: latestLogTimeRef.current, direction: CALL_LOG_DIRECTION.FORWARD });
                sendSeenCallLog();
            } else {
                fetchLogs({});
            }
        }
    }, [activeStack, refreshCallLogFlag, isAppOnline]);

    // Initial Fetch (First time only)
    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true;
            setLoading(true);
            fetchLogs({});
            sendSeenCallLog();
        }
    }, []);

    const loaderRef = React.useRef(null);

    useEffect(() => {
        let observer;
        const currentLoader = loaderRef.current;

        if (!hasMore || loading || isFetchingNext || allCallLogs.length === 0) return;

        observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    // Disconnect immediately to iprevent multiple triggers while fetching
                    observer.disconnect();
                    setIsFetchingNext(true);
                    const oldestLog = allCallLogs[allCallLogs.length - 1];
                    const oldestLogTime = oldestLog ? oldestLog.callTime : Date.now();
                    fetchLogs({ lastSyncTime: oldestLogTime, direction: CALL_LOG_DIRECTION.BACKWARD });
                }
            },
            {
                root: null,
                rootMargin: '100px', // Pre-fetch slightly before it enters view
                threshold: 0.1,
            },
        );

        if (currentLoader) {
            observer.observe(currentLoader);
        }

        return () => {
            if (observer && currentLoader) {
                observer.unobserve(currentLoader);
                observer.disconnect();
            }
        };
    }, [hasMore, loading, isFetchingNext, allCallLogs]);

    // Update derived UI grouped data whenever the raw logs change
    useEffect(() => {
        const formatted = transformAndGroupLogs(allCallLogs);
        setData(formatted);

        const missingUserIds = new Set();
        allCallLogs.forEach(log => {
            (log.callParticipants || []).forEach(p => {
                const rawIdentifier = p.username || p.userId;
                const userIdentifier = rawIdentifier ? rawIdentifier.split('@')[0] : '';
                if (userIdentifier && !usersMap[userIdentifier]) {
                    missingUserIds.add(userIdentifier);
                }
            });
        });

        if (missingUserIds.size > 0) {
            fetchUsersInfo(Array.from(missingUserIds));
        }
    }, [allCallLogs, usersMap]);

    const toggleFab = () => setIsFabExpanded(!isFabExpanded);

    const filteredData = data
        .map(group => {
            const filteredLogs = group.logs.filter(log => {
                const displayName = log.groupName || log.participants[0]?.name || '';
                return displayName.toLowerCase().includes(searchQuery.toLowerCase());
            });
            return { ...group, logs: filteredLogs };
        })
        .filter(group => group.logs.length > 0);

    const onDeleteCallLogResponse = (isClearAll, idsToDelete, err) => {
        if (err) {
            logger.error('Failed to delete call logs:', err);
            return;
        }
        if (isClearAll) {
            setData([]);
            setAllCallLogs([]);
        } else {
            setData(getFilteredDataLogs(data, idsToDelete));
            setAllCallLogs(prev => getFilteredPrevLogs(prev, idsToDelete));
        }
        showSucessToast(t('toast.ALL_SELECTED_MESSAGES_DELETED'));
    };

    // Delete handler triggering API and optimistic UI updates
    const handleDeleteConfirm = idsToDelete => {
        const isClearAll = idsToDelete.length === viewModel.totalLogsCount;

        const payload = {
            callIds: isClearAll ? [] : idsToDelete,
            clearAll: isClearAll,
        };

        // Call API
        deleteCallLog(payload, (success, err) => onDeleteCallLogResponse(isClearAll, idsToDelete, err));
    };

    const handleDeleteLogsClick = () => {
        const isAllSelected = viewModel.selectedLogIds.size === viewModel.totalLogsCount;
        const count = viewModel.selectedLogIds.size;

        setAlertModal({
            id: 'delete_call_logs',
            visible: true,
            title: t('calls.LOGS.DELETE_CALL_LOGS'),
            description: isAllSelected
                ? t('calls.LOGS.SURE_DELETE_ALL_LOGS')
                : t('calls.LOGS.SURE_DELETE_LOGS', { count }),
            icon: (
                <div className="alert-modal-icon-inner ct-flexCenter">
                    <IconTrash />
                </div>
            ),
            customClassName: 'delete-account-error-modal alert-modal-delete-v2 ',
            noButton: t('calls.LOGS.CANCEL'),
            noAction: () => {
                resetAlertModal();
            },
            yesButton: t('calls.LOGS.DELETE'),
            yesAction: () => {
                viewModel.confirmDelete(handleDeleteConfirm);
                resetAlertModal();
            },
            onRequestClose: () => resetAlertModal(),
        });
    };

    const activeCallLog = useSelector(state => state.activeCallLogData.activeCallLog);
    const activeLogId = activeCallLog?.id;

    const handleLogClick = logData => {
        dispatch(setActiveCallLog(logData));
    };

    const handleCallBack = log => {
        const callType = log.isVideo ? CALL_TYPE.VIDEO : CALL_TYPE.AUDIO;
        const isGroup = !!(log.groupId || log.participants?.length > 1);

        if (isGroup) {
            const userIds = (log.participants || []).map(p => p.userId).filter(Boolean);
            const groupId = log.groupId || undefined;
            const maxUsers = parseInt(process.env.REACT_APP_MAX_USERS_CALL) || 4;
            if (userIds.length > maxUsers - 1) {
                toast.error(`Maximum ${maxUsers} members allowed in a call`);
                return;
            }
            dispatch(requestCall({ userIds, groupId, callType, isGroup: true }));
        } else {
            const bareId = log.participants?.[0]?.userId;
            if (bareId) {
                dispatch(requestCall({ userId: bareId, callType }));
            }
        }
    };

    const renderContent = () => {
        if (loading && allCallLogs.length === 0) {
            return (
                <section className="callScreenSection">
                    <div className="callScreenList ct-flex ct-fcol">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <CallItemSkeleton key={`skele-call-log-${i * 2 + 1}`} />
                        ))}
                    </div>
                </section>
            );
        }
        if (filteredData.length > 0) {
            return (
                <span className="unknown-chat-roster">
                    <ul className="chat-list-ul">
                        {filteredData.map(group => (
                            <Fragment key={group.label}>
                                <h2
                                    className="callScreenSectionTitle"
                                    style={{
                                        padding: '10px 16px',
                                        fontSize: '13px',
                                        color: '#666',
                                        background: '#f8f9fa',
                                        marginBottom: '10px',
                                    }}
                                >
                                    {group.label}
                                </h2>
                                {group.logs.map(log => (
                                    <CallLogItem
                                        key={log.id}
                                        log={log}
                                        isSelectionMode={viewModel.isSelectionMode}
                                        isSelected={viewModel.selectedLogIds.has(log.id)}
                                        isActive={activeLogId === log.id}
                                        onToggleSelection={viewModel.handleToggleLogSelection}
                                        onClick={handleLogClick}
                                        onCallBack={handleCallBack}
                                    />
                                ))}
                            </Fragment>
                        ))}
                    </ul>
                </span>
            );
        }
        return (
            <EmptyPlaceholder
                icon={<IconEmptyplaceholderCall />}
                title={t('calls.LOGS.NO_HISTORY')}
                description={t('calls.LOGS.NEW_CALLS_APPEAR')}
                className="callScreenEmptyState"
            />
        );
    };

    return (
        <div className="callScreenContainer">
            <CallLogHeader
                dataLength={data.length}
                isSelectionMode={viewModel.isSelectionMode}
                selectedCount={viewModel.selectedLogIds.size}
                totalCount={viewModel.totalLogsCount}
                onSearchChange={setSearchQuery}
                onToggleSelectionMode={viewModel.handleToggleSelectionMode}
                onSelectAllToggle={viewModel.handleSelectAllToggle}
                onDeleteSelected={handleDeleteLogsClick}
            />

            <div className="chat-list-container">
                {!viewModel.isSelectionMode && (
                    <RecentSearch placeHolder={'Search for contacts, chat and more'} search={setSearchQuery} />
                )}
                {isOffline && (
                    <div className="networkoffline">
                        <i className="icon">
                            <IconNoInternet />
                        </i>
                        <div>
                            <h4>Computer not connected</h4>
                            <p>Make sure your computer has an active internet connection.....</p>
                        </div>
                    </div>
                )}
                <div className={`chat-list ${isOffline ? 'offline' : ''}`}>
                    {renderContent()}

                    {isFetchingNext && (
                        <div className="callScreenList">
                            <CallItemSkeleton />
                            <CallItemSkeleton />
                        </div>
                    )}
                    {hasMore && !loading && allCallLogs.length > 0 && (
                        <div ref={loaderRef} className="ct-flexCenter ct-py20 ct-wFull">
                            <p style={{ color: '#888', fontSize: '14px' }}>
                                {t('calls.LOGS.LOADING_MORE') || 'Loading more...'}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <div className="callScreenFabContainer">
                <div className={`callScreenFabMenu ${isFabExpanded ? 'callScreenFabMenuOpen' : ''}`}>
                    <button
                        className="callScreenFabAction"
                        title="New Audio Call"
                        onClick={() => {
                            setIsFabExpanded(false);
                            handleNewCallNavigation && handleNewCallNavigation('audio');
                        }}
                    >
                        <i className="ct-icon ct-xl">
                            <IconGrayPhone />
                        </i>
                    </button>
                    <button
                        className="callScreenFabAction"
                        title="New Video Call"
                        onClick={() => {
                            setIsFabExpanded(false);
                            handleNewCallNavigation && handleNewCallNavigation('video');
                        }}
                    >
                        <i className="ct-icon ct-xl">
                            <IconGrayVideo />
                        </i>
                    </button>
                </div>
                <button className={`callScreenFab ${isFabExpanded ? 'callScreenFabActive' : ''}`} onClick={toggleFab}>
                    {isFabExpanded ? (
                        <i className="ct-icon">
                            <IconNewWhiteClose />
                        </i>
                    ) : (
                        <i className="ct-icon">
                            <IconWhitePlus />
                        </i>
                    )}
                </button>
            </div>
        </div>
    );
};

export default CallScreen;
